import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Heart, Users, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Charity: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Determine locale-specific WhatsApp number
  const isEnglish = (i18n.language || i18n.resolvedLanguage || 'en').toLowerCase().startsWith('en');
  const whatsappNumber = isEnglish ? '61401461019' : '6281337329299';

  const stats = [
    { icon: Gift, value: '100+', label: t('charity.stats.cakesDonated') },
    { icon: Users, value: '5+', label: t('charity.stats.eventsSupported') },
    { icon: Heart, value: '1000+', label: t('charity.stats.smilesDelivered') },
  ];

  const supportOptions = [
    {
      icon: Gift,
      title: t('charity.support.cakeDonation.title'),
      description: t('charity.support.cakeDonation.description'),
    },
    {
      icon: Heart,
      title: t('charity.support.events.title'),
      description: t('charity.support.events.description'),
    },
    {
      icon: Users,
      title: t('charity.support.participation.title'),
      description: t('charity.support.participation.description'),
    },
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-premium-50">
      {/* Hero Section */}
      <section
        className="pt-32 pb-20 bg-cover bg-center"
        style={{ backgroundImage: "url('/path-to-your-image/charity-hero.jpg')" }} // Replace with actual hero image
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white bg-black bg-opacity-50 rounded-xl p-8 md:p-12 max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-4">
              {t('charity.title')}
            </h1>
            <p className="text-xl mt-4">
              {t('charity.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <stat.icon className="h-10 w-10 text-burgundy-600 mx-auto mb-4" />
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Program Section */}
      <section className="py-20 bg-gradient-to-br from-burgundy-50 to-cream-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
                {t('charity.about.title')}
              </h2>
              <div className="w-24 h-1 gold-gradient rounded-full mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 leading-relaxed">
                {t('charity.about.description')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Support Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
              {t('charity.support.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {supportOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center p-8 rounded-xl bg-gradient-to-br from-cream-50 to-burgundy-50 hover:shadow-lg transition-shadow duration-300"
              >
                <option.icon className="h-12 w-12 text-burgundy-600 mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{option.title}</h3>
                <p className="text-gray-600 leading-relaxed">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section (form removed) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              {t('charity.cta.title')}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {t('charity.cta.subtitle')}
            </p>
            <a
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-green-500 text-white font-semibold rounded-full hover:bg-green-600 transition-colors"
            >
              <Phone size={20} />
              {t('contact.whatsappButton')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Charity;
